/*
 * BulkImport Saga
 */

import {
  takeLatest,
  call,
  put,
  select,
  take,
  all,
  delay,
  race,
  spawn,
} from 'redux-saga/effects';
import { eventChannel, END } from 'redux-saga';

import { fileUploadAPI, bulkImportAPI, fileUtils } from './api';
import bulkImportWebSocket from './websocket';
import { getErrorMessage } from '../../utils/error.util';
import { addMessage } from '../ToastMessagesManager/actions';
import {
  uploadFileProgressAct,
  getRequiredFieldsAct,
  websocketStatusAct,
  jobProgressAct,
  uploadFileAct,
  analyzeFileAct,
  createImportJobAct,
  websocketConnectAct,
  cleanupAct,
} from './actions';

import { CHUNK_SIZE } from './constants';

import { makeSelectUser } from '../AuthManager/selectors';
import { selectBulkImport } from './selectors';

/**
 * File Upload Saga
 */
function* uploadFileSaga(action) {
  const { payload } = action;
  const { loadingState, file } = payload;

  // Only handle the start upload action to avoid self-cancellation
  if (loadingState !== 1) {
    return;
  }

  if (loadingState === 1) {
    try {
      // Validate file
      yield call(fileUtils.validateFile, file);

      // Convert file to chunks
      const { chunks, totalChunks, fileName, fileSize, mimeType } = yield call(
        fileUtils.fileToChunks,
        file,
        CHUNK_SIZE,
      );

      // Initialize upload
      const initResponse = yield call(fileUploadAPI.initializeUpload, {
        fileName,
        fileSize,
        mimeType,
        totalChunks,
      });

      const { uploadId } = initResponse;

      // Upload chunks
      for (let i = 0; i < chunks.length; i += 1) {
        const chunk = chunks[i];

        yield call(fileUploadAPI.uploadChunk, {
          uploadId,
          chunkIndex: chunk.index,
          totalChunks,
          chunkSize: chunk.size,
          chunkData: chunk.data,
        });

        const progress = Math.round(((i + 1) / totalChunks) * 80); // Reserve 20% for finalization
        yield put(uploadFileProgressAct({ progress }));
      }

      // Finalize upload
      const finalizeResponse = yield call(fileUploadAPI.finalizeUpload, {
        uploadId,
        metadata: { originalName: fileName },
      });

      // Success - dispatch both actions together atomically
      yield all([
        put(
          uploadFileAct(
            2,
            finalizeResponse,
            100,
            uploadId,
            finalizeResponse.id,
          ),
        ),
        put(analyzeFileAct(1)),
      ]);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      // Dispatch error message and failure state in parallel
      yield all([
        put(addMessage({ message: errorMessage, variant: 'error' })),
        put(uploadFileAct(3, file, 0, null, null)),
      ]);
    }
  }
}

/**
 * File Analysis Saga
 */
function* analyzeFileSaga(action) {
  const { payload } = action;
  const { loadingState } = payload;

  // Only handle the start analyze action to avoid self-cancellation
  if (loadingState !== 1) {
    return;
  }

  if (loadingState === 1) {
    try {
      const state = yield select(selectBulkImport);
      const { fileId, importType } = state;

      if (!fileId) {
        throw new Error('No file uploaded for analysis');
      }

      // Get required fields and analyze file in parallel
      const analysisResponse = yield call(bulkImportAPI.analyzeFile, {
        fileId,
        importType,
      });

      // Dispatch both success actions
      yield all([
        put(getRequiredFieldsAct(2, analysisResponse.requiredFields)),
        put(analyzeFileAct(2, analysisResponse)),
      ]);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      // Dispatch error message and failure states in parallel
      yield all([
        put(addMessage({ message: errorMessage, variant: 'error' })),
        put(getRequiredFieldsAct(3)),
        put(analyzeFileAct(3, null)),
      ]);
    }
  }
}

/**
 * Create Import Job Saga
 */
function* createImportJobSaga(action) {
  const { payload } = action;
  const { loadingState, columnMapping } = payload;

  // Only handle the start create job action to avoid self-cancellation
  if (loadingState !== 1) {
    return;
  }

  if (loadingState === 1) {
    try {
      const state = yield select(selectBulkImport);
      const { fileId, importType } = state;

      if (!fileId) {
        throw new Error('No file available for import');
      }

      if (!columnMapping || Object.keys(columnMapping).length === 0) {
        throw new Error('Column mapping is required');
      }

      const response = yield call(bulkImportAPI.createJob, {
        fileId,
        importType,
        columnMapping,
      });

      // Dispatch success and connect to WebSocket in parallel
      yield all([
        put(createImportJobAct(2, columnMapping, response)),
        put(websocketConnectAct()),
      ]);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      // Dispatch error message and failure state in parallel
      yield all([
        put(addMessage({ message: errorMessage, variant: 'error' })),
        put(createImportJobAct(3, columnMapping, null)),
      ]);
    }
  }
}

/**
 * WebSocket Connection Saga with Fallback Polling
 */
function* connectWebSocketSaga() {
  const user = yield select(makeSelectUser());
  if (!user?.id) {
    console.warn('User not authenticated for WebSocket connection');
    yield put(websocketStatusAct(false));
    return;
  }

  const state = yield select(selectBulkImport);
  const { currentJob } = state;

  if (!currentJob?.jobId) {
    console.warn('No current job available for processing');
    return;
  }

  let jobStarted = false;

  // Try WebSocket connection with timeout
  const result = yield race({
    wsResult: call(tryWebSocketConnection, user.id, currentJob),
    timeout: delay(10000), // 10 second timeout
  });

  if (result.wsResult && result.wsResult.connected) {
    console.warn('WebSocket connection successful, using real-time updates');
    jobStarted = result.wsResult.jobStarted || currentJob.status !== 'pending';
    return;
  }

  // WebSocket failed or timed out, fall back to polling
  console.warn(
    'WebSocket connection failed or timed out, falling back to polling',
  );
  yield put(websocketStatusAct(false));
  yield put(
    addMessage({
      message: 'Real-time updates unavailable, using periodic refresh',
      variant: 'warning',
    }),
  );

  // Start job processing and begin polling (only if job hasn't been started)
  try {
    if (!jobStarted && currentJob.status === 'pending') {
      yield call(bulkImportAPI.startJob, currentJob.jobId);
      console.warn(
        `Started processing import job ${currentJob.jobId} with polling fallback`,
      );
    }

    // Start polling for job progress
    yield spawn(pollJobProgressSaga, currentJob.jobId);
  } catch (error) {
    console.error(`Failed to start import job ${currentJob.jobId}:`, error);
    const errorMessage = getErrorMessage(error);
    yield put(addMessage({ message: errorMessage, variant: 'error' }));
  }
}

/**
 * Try WebSocket Connection
 */
function* tryWebSocketConnection(userId, currentJob) {
  let jobStarted = false;
  let socketEventChannel;

  try {
    socketEventChannel = yield call(createWebSocketChannel, userId);

    // Listen for WebSocket events
    while (true) {
      const event = yield take(socketEventChannel);

      switch (event.type) {
        case 'connected': {
          yield put(websocketStatusAct(true));

          // Start job processing once WebSocket is connected
          if (currentJob.status === 'pending') {
            try {
              yield call(bulkImportAPI.startJob, currentJob.jobId);
              jobStarted = true;
              console.warn(`Started processing import job ${currentJob.jobId}`);
            } catch (error) {
              console.error(
                `Failed to start import job ${currentJob.jobId}:`,
                error,
              );
              const errorMessage = getErrorMessage(error);
              yield put(
                addMessage({ message: errorMessage, variant: 'error' }),
              );
              // Close socket and return failure
              if (socketEventChannel) {
                socketEventChannel.close();
              }
              return { connected: false, jobStarted: false };
            }
          } else {
            jobStarted = true; // Job already started
          }
          break;
        }
        case 'jobStarted':
        case 'jobProgress':
        case 'jobCompleted':
        case 'jobFailed':
          yield put(jobProgressAct(event.payload));

          // Return success when job is completed or failed
          if (['jobCompleted', 'jobFailed'].includes(event.type)) {
            if (socketEventChannel) {
              socketEventChannel.close();
            }
            return { connected: true, jobStarted };
          }
          break;
        case 'error': {
          yield all([put(websocketStatusAct(false))]);
          if (socketEventChannel) {
            socketEventChannel.close();
          }
          return { connected: false, jobStarted };
        }
        case 'disconnected':
          yield put(websocketStatusAct(false));
          if (socketEventChannel) {
            socketEventChannel.close();
          }
          return { connected: false, jobStarted };
        default:
          break;
      }
    }
  } catch (error) {
    console.warn('WebSocket connection error:', error.message);
    if (socketEventChannel) {
      socketEventChannel.close();
    }
    return { connected: false, jobStarted };
  }
}

/**
 * Polling Fallback Saga
 */
function* pollJobProgressSaga(jobId) {
  let consecutiveErrors = 0;
  const maxConsecutiveErrors = 3;

  try {
    let isJobComplete = false;

    while (!isJobComplete) {
      try {
        // Poll job status every 2 seconds
        yield delay(2000);

        const jobDetails = yield call(bulkImportAPI.getJob, jobId);

        // Reset error counter on successful request
        consecutiveErrors = 0;

        // Update job progress (API now returns consistent format)
        yield put(jobProgressAct(jobDetails));

        // Check if job is complete
        if (['completed', 'failed', 'cancelled'].includes(jobDetails.status)) {
          isJobComplete = true;
          console.warn(
            `Polling completed for job ${jobId} with status: ${jobDetails.status}`,
          );
        }
      } catch (error) {
        consecutiveErrors += 1;
        console.error(
          `Error polling job ${jobId} (attempt ${consecutiveErrors}):`,
          error,
        );

        // If we've had too many consecutive errors, stop polling and report error
        if (consecutiveErrors >= maxConsecutiveErrors) {
          console.error(
            `Too many consecutive errors polling job ${jobId}, stopping polling`,
          );
          const errorMessage = getErrorMessage(error);
          yield put(
            addMessage({
              message: `Failed to get job status: ${errorMessage}`,
              variant: 'error',
            }),
          );
          break;
        }

        // Wait longer on error, but not too long to delay error reporting
        yield delay(3000);
      }
    }
  } catch (error) {
    console.error(`Polling saga error for job ${jobId}:`, error);
    const errorMessage = getErrorMessage(error);
    yield put(addMessage({ message: errorMessage, variant: 'error' }));
  }
}

/**
 * Create WebSocket Event Channel
 */
function createWebSocketChannel(userId) {
  return eventChannel(emitter => {
    const callbacks = {
      connected: () => emitter({ type: 'connected' }),
      disconnected: data => emitter({ type: 'disconnected', payload: data }),
      error: data => emitter({ type: 'error', payload: data }),
      jobStarted: data => emitter({ type: 'jobStarted', payload: data }),
      jobProgress: data => emitter({ type: 'jobProgress', payload: data }),
      jobCompleted: data => emitter({ type: 'jobCompleted', payload: data }),
      jobFailed: data => emitter({ type: 'jobFailed', payload: data }),
    };

    // Connect to WebSocket
    bulkImportWebSocket.connect(userId, callbacks).catch(error => {
      console.warn('Failed to establish WebSocket connection:', error.message);
      emitter({ type: 'error', payload: { error: error.message } });
    });

    // Return cleanup function
    return () => {
      bulkImportWebSocket.disconnect();
      emitter(END);
    };
  });
}

/**
 * Cleanup Saga (for component unmount)
 */
function* cleanupSaga() {
  try {
    // Disconnect WebSocket
    yield call(() => bulkImportWebSocket.disconnect());

    // Reset WebSocket status
    yield put(websocketStatusAct(false));

    console.warn('BulkImport cleanup completed');
  } catch (error) {
    console.error('Error during BulkImport cleanup:', error);
  }

  // Note: Polling sagas will automatically stop when their jobs complete
  // or when the main saga is cancelled (component unmount)
}

/**
 * Root Saga
 */
export default function* bulkImportSaga() {
  yield takeLatest(uploadFileAct.toString(), uploadFileSaga);
  yield takeLatest(analyzeFileAct.toString(), analyzeFileSaga);
  yield takeLatest(createImportJobAct.toString(), createImportJobSaga);
  yield takeLatest(websocketConnectAct.toString(), connectWebSocketSaga);
  yield takeLatest(cleanupAct.toString(), cleanupSaga);
}
